import React from 'react';
import styles from './Tenants.module.css';
import { DefaultButton, Icon, Modal, PrimaryButton } from '@fluentui/react';
import { Notification } from '../Components/Notification';
import { CreateTenantModal } from '../Components/CreateTenantModal';
import { EntityLink } from '../Components/EntityLink';
import { StandardTable } from '../Components/StandardTable';
import { TenantDirectoryTable } from '../Components/TenantDirectoryTable';
import { useConfirmAction } from '../hooks';
import { API_BASE } from '../Services/constants';
import { apiClient } from '../Services/api-client';
import { useLocation } from 'react-router-dom';

interface Portfolio {
  portfolioId: string;
  friendlyName: string;
}

interface Community {
  communityId: string;
  friendlyName: string;
  displayName?: string;
  address?: string;
}

interface TenantSummary {
  emailNorm: string;
  firstName?: string;
  lastName?: string;
}

export const Tenants: React.FC = () => {
  const location = useLocation();
  const tenantDataEvent = 'tenant-data-updated';
  const [portfolios, setPortfolios] = React.useState<Portfolio[]>([]);
  const [communities, setCommunities] = React.useState<Community[]>([]);
  const [filterCommunities, setFilterCommunities] = React.useState<Community[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [tenantsLoading, setTenantsLoading] = React.useState(false);
  const [accessLoading, setAccessLoading] = React.useState(false);
  const [notification, setNotification] = React.useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [lookupEmail, setLookupEmail] = React.useState('');
  const [tenantAccess, setTenantAccess] = React.useState<any>(null);
  const [editFirstName, setEditFirstName] = React.useState('');
  const [editLastName, setEditLastName] = React.useState('');
  const [showIds, setShowIds] = React.useState(false);
  const [portfolioNameMap, setPortfolioNameMap] = React.useState<Record<string, string>>({});
  const [communityNameMap, setCommunityNameMap] = React.useState<Record<string, string>>({});
  const [allTenants, setAllTenants] = React.useState<TenantSummary[]>([]);
  const [filterPortfolioId, setFilterPortfolioId] = React.useState('');
  const [filterCommunityId, setFilterCommunityId] = React.useState('');
  const [searchTerm, setSearchTerm] = React.useState('');
  const [accessSet, setAccessSet] = React.useState<Set<string>>(new Set());
  const [tenantAccessMap, setTenantAccessMap] = React.useState<
    Record<string, Array<{ portfolioId: string; communityId: string }>>
  >({});
  const [isManageOpen, setIsManageOpen] = React.useState(false);
  const [modalPortfolioId, setModalPortfolioId] = React.useState('');
  const [modalCommunityId, setModalCommunityId] = React.useState('');
  const [modalCommunities, setModalCommunities] = React.useState<Community[]>([]);
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [createDefaults, setCreateDefaults] = React.useState<{
    firstName: string;
    lastName: string;
    email: string;
    portfolioId: string;
    communityId: string;
  }>({
    firstName: '',
    lastName: '',
    email: '',
    portfolioId: '',
    communityId: '',
  });
  const { openConfirm, dialog: confirmDialog } = useConfirmAction();

  const capitalizeFirst = React.useCallback((value: string) => {
    if (!value) {
      return value;
    }
    return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
  }, []);

  const formatName = React.useCallback(
    (value?: string) => (value ? capitalizeFirst(value) : ''),
    [capitalizeFirst]
  );

  const broadcastTenantDataUpdate = React.useCallback(() => {
    localStorage.setItem('tenantDataVersion', Date.now().toString());
    window.dispatchEvent(new Event(tenantDataEvent));
  }, [tenantDataEvent]);

  const loadPortfolios = React.useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE}/portfolios`);
      setPortfolios(response.data || []);
    } catch (error) {
      console.error('Failed to load portfolios');
    }
  }, []);

  const loadCommunities = React.useCallback(async (portfolioId: string) => {
    if (!portfolioId) {
      setCommunities([]);
      return;
    }
    try {
      const response = await apiClient.get(`${API_BASE}/portfolios/${portfolioId}/communities`);
      setCommunities(response.data || []);
    } catch (error) {
      console.error('Failed to load communities');
    }
  }, []);

  const loadListCommunities = React.useCallback(
    async (portfolioId: string) => {
      if (!portfolioId) {
        setFilterCommunities([]);
        return;
      }
      try {
        const response = await apiClient.get(`${API_BASE}/portfolios/${portfolioId}/communities`);
        setFilterCommunities(response.data || []);
      } catch (error) {
        console.error('Failed to load communities for listing');
      }
    },
    []
  );

  const loadModalCommunities = React.useCallback(
    async (portfolioId: string) => {
      if (!portfolioId) {
        setModalCommunities([]);
        return;
      }
      try {
        const response = await apiClient.get(`${API_BASE}/portfolios/${portfolioId}/communities`);
        setModalCommunities(response.data || []);
      } catch (error) {
        console.error('Failed to load communities for modal');
      }
    },
    []
  );

  const loadAllTenants = React.useCallback(async () => {
    try {
      setTenantsLoading(true);
      const response = await apiClient.get(`${API_BASE}/tenant-access/tenants`);
      setAllTenants(response.data || []);
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to load tenants',
      });
      setAllTenants([]);
    } finally {
      setTenantsLoading(false);
    }
  }, []);

  const loadTenantAccessMap = React.useCallback(
    async (tenants: TenantSummary[]) => {
      if (!tenants.length) {
        setTenantAccessMap({});
        return;
      }

      const results: Record<string, Array<{ portfolioId: string; communityId: string }>> = {};

      await Promise.all(
        tenants.map(async (tenant) => {
          if (!tenant.emailNorm) {
            return;
          }
          try {
            const response = await apiClient.get(
              `${API_BASE}/tenant-access/tenants/${encodeURIComponent(tenant.emailNorm)}/grants`
            );
            results[tenant.emailNorm] = response.data?.granted?.communityAccess || [];
            return;
          } catch (error: any) {
            if (error?.response?.status === 404) {
              results[tenant.emailNorm] = [];
              return;
            }
            // Ignore failures per-tenant to avoid blocking the list.
            results[tenant.emailNorm] = [];
          }
        })
      );

      setTenantAccessMap(results);
    },
    []
  );

  React.useEffect(() => {
    loadPortfolios();
    loadAllTenants();
  }, [loadPortfolios, loadAllTenants]);

  React.useEffect(() => {
    const refreshTenants = () => {
      loadAllTenants();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'tenantDataVersion') {
        refreshTenants();
      }
    };

    const handleEvent = () => {
      refreshTenants();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(tenantDataEvent, handleEvent);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(tenantDataEvent, handleEvent);
    };
  }, [loadAllTenants]);

  React.useEffect(() => {
    loadTenantAccessMap(allTenants);
  }, [allTenants, loadTenantAccessMap]);

  React.useEffect(() => {
    const loadAccessSet = async () => {
      if (!filterPortfolioId) {
        setAccessSet(new Set());
        return;
      }

      try {
        setAccessLoading(true);
        const url = filterCommunityId
          ? `${API_BASE}/tenant-access/community/${encodeURIComponent(
              filterCommunityId
            )}/tenants?portfolioId=${encodeURIComponent(filterPortfolioId)}`
          : `${API_BASE}/tenant-access/portfolio/${encodeURIComponent(filterPortfolioId)}/tenants`;

          const response = await apiClient.get(url);
          const data = response.data;
        const nextSet = new Set<string>();
        (data || []).forEach((item: TenantSummary) => {
          if (item.emailNorm) {
            nextSet.add(item.emailNorm);
          }
        });
        setAccessSet(nextSet);
      } catch (error) {
        setNotification({
          type: 'error',
          message: 'Failed to load tenant access list',
        });
        setAccessSet(new Set());
      } finally {
        setAccessLoading(false);
      }
    };

    loadAccessSet();
  }, [filterPortfolioId, filterCommunityId]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const portfolioId = params.get('portfolioId') || '';
    const communityId = params.get('communityId') || '';
    const email = params.get('email') || '';
    const manageEmail = params.get('manageEmail') || '';
    const firstName = params.get('firstName') || '';
    const lastName = params.get('lastName') || '';
    const fullNameParam = params.get('fullName') || '';
    const [fullFirst, ...fullRest] = fullNameParam.trim().split(/\s+/).filter(Boolean);
    const fullLast = fullRest.join(' ');

    if (manageEmail) {
      setTimeout(() => {
        openManageModal(manageEmail);
      }, 0);
      return;
    }

    if (portfolioId || communityId || email || fullNameParam || firstName || lastName) {
      setTimeout(() => {
        openCreateTenantModal({
          portfolioId,
          communityId,
          email,
          firstName: firstName
            ? capitalizeFirst(firstName)
            : fullFirst
              ? capitalizeFirst(fullFirst)
              : '',
          lastName: lastName
            ? capitalizeFirst(lastName)
            : fullLast
              ? capitalizeFirst(fullLast)
              : '',
        });
      }, 0);
    }
  }, [location.search, capitalizeFirst]);

  React.useEffect(() => {
    const nextMap: Record<string, string> = {};
    portfolios.forEach((portfolio) => {
      nextMap[portfolio.portfolioId] = portfolio.friendlyName || portfolio.portfolioId;
    });
    setPortfolioNameMap(nextMap);
  }, [portfolios]);

  React.useEffect(() => {
    const loadCommunityNames = async () => {
      const accessEntries: Array<{ portfolioId: string; communityId: string }> = [];

      if (tenantAccess?.granted?.communityAccess?.length) {
        accessEntries.push(...tenantAccess.granted.communityAccess);
      }

      Object.values(tenantAccessMap).forEach((accesses) => {
        accessEntries.push(...accesses);
      });

      const portfolioIds = Array.from(
        new Set(accessEntries.map((access) => access.portfolioId).filter(Boolean))
      );

      if (!portfolioIds.length) {
        setCommunityNameMap({});
        return;
      }

      const nextMap: Record<string, string> = {};

      await Promise.all(
        portfolioIds.map(async (portfolioId) => {
          try {
            const response = await apiClient.get(
              `${API_BASE}/portfolios/${portfolioId}/communities`
            );
            const data = response.data;

            (data || []).forEach((community: any) => {
              const name =
                community.friendlyName ||
                community.displayName ||
                community.address ||
                community.communityId;
              nextMap[`${portfolioId}:${community.communityId}`] = name;
            });
          } catch (error) {
            console.error('Failed to load communities for portfolio', portfolioId);
          }
        })
      );

      setCommunityNameMap(nextMap);
    };

    loadCommunityNames();
  }, [tenantAccess, tenantAccessMap]);

  const handleCopy = async (value: string, label = 'Value') => {
    try {
      await navigator.clipboard.writeText(value);
      setNotification({
        type: 'success',
        message: `${label} copied to clipboard`,
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: `Failed to copy ${label.toLowerCase()}`,
      });
    }
  };

  const getAccessList = (email: string) => {
    const accessList = tenantAccessMap[email] || [];
    if (!filterPortfolioId && !filterCommunityId) {
      return accessList;
    }

    return accessList.filter((access) => {
      if (filterPortfolioId && access.portfolioId !== filterPortfolioId) {
        return false;
      }
      if (filterCommunityId && access.communityId !== filterCommunityId) {
        return false;
      }
      return true;
    });
  };

  const getTenantSearchText = (tenant: TenantSummary) => {
    const name = `${tenant.firstName || ''} ${tenant.lastName || ''}`.trim().toLowerCase();
    const email = (tenant.emailNorm || '').toLowerCase();
    const accessList = getAccessList(tenant.emailNorm)
      .map((access) => {
        const key = `${access.portfolioId}:${access.communityId}`;
        const portfolioName = portfolioNameMap[access.portfolioId] || access.portfolioId;
        const communityName = communityNameMap[key] || access.communityId;
        return `${portfolioName} ${communityName} ${access.portfolioId} ${access.communityId}`;
      })
      .join(' ')
      .toLowerCase();

    return `${name} ${email} ${accessList}`.trim();
  };

  const handleListPortfolioChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const portfolioId = e.target.value;
    setFilterPortfolioId(portfolioId);
    setFilterCommunityId('');
    loadListCommunities(portfolioId);
  };

  const handleListCommunityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterCommunityId(e.target.value);
  };

  const handleClearFilters = () => {
    setFilterPortfolioId('');
    setFilterCommunityId('');
    setFilterCommunities([]);
    setAccessSet(new Set());
    setSearchTerm('');
  };

  const lookupTenantAccess = async (email: string) => {
    try {
      setLoading(true);
      const response = await apiClient.get(
        `${API_BASE}/tenant-access/tenants/${encodeURIComponent(email)}/grants`
      );
      const data = response.data;
      setTenantAccess(data);
      setEditFirstName(formatName(data?.firstName));
      setEditLastName(formatName(data?.lastName));
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setNotification({
          type: 'error',
          message: 'Tenant not found or no access configured',
        });
      } else {
        setNotification({
          type: 'error',
          message: 'An error occurred',
        });
      }
      setTenantAccess(null);
      setEditFirstName('');
      setEditLastName('');
    } finally {
      setLoading(false);
    }
  };

  const openCreateTenantModal = (defaults?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    portfolioId?: string;
    communityId?: string;
  }) => {
    setCreateDefaults({
      firstName: defaults?.firstName || '',
      lastName: defaults?.lastName || '',
      email: defaults?.email || '',
      portfolioId: defaults?.portfolioId || '',
      communityId: defaults?.communityId || '',
    });
    setIsCreateOpen(true);
  };

  const updateTenantListName = (
    emailNorm: string,
    firstName?: string,
    lastName?: string
  ) => {
    setAllTenants((prev) =>
      prev.map((tenant) =>
        tenant.emailNorm === emailNorm
          ? { ...tenant, firstName: firstName || '', lastName: lastName || '' }
          : tenant
      )
    );
  };

  const updateAccessTracking = (
    emailNorm: string,
    accessList: Array<{ portfolioId: string; communityId: string }>
  ) => {
    setTenantAccessMap((prev) => ({
      ...prev,
      [emailNorm]: accessList,
    }));

    if (!filterPortfolioId) {
      return;
    }

    const matches = accessList.some((access) => {
      if (access.portfolioId !== filterPortfolioId) {
        return false;
      }
      if (filterCommunityId && access.communityId !== filterCommunityId) {
        return false;
      }
      return true;
    });

    setAccessSet((prev) => {
      const next = new Set(prev);
      if (matches) {
        next.add(emailNorm);
      } else {
        next.delete(emailNorm);
      }
      return next;
    });
  };

  const handleUpdateTenantName = async () => {
    if (!tenantAccess?.emailNorm) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.put(
        `${API_BASE}/tenant-access/tenants/${encodeURIComponent(tenantAccess.emailNorm)}`,
        {
          firstName: editFirstName,
          lastName: editLastName,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const data = response.data;
      setTenantAccess(data);
      setEditFirstName(formatName(data?.firstName));
      setEditLastName(formatName(data?.lastName));
      updateTenantListName(tenantAccess.emailNorm, data?.firstName, data?.lastName);
      setNotification({
        type: 'success',
        message: 'Tenant name updated',
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to update tenant name',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCommunityAccess = async (portfolioId: string, communityId: string) => {
    if (!tenantAccess?.emailNorm) {
      return;
    }

    try {
      setLoading(true);
        await apiClient.delete(
          `${API_BASE}/tenant-access/tenants/${encodeURIComponent(
            tenantAccess.emailNorm
          )}/communities/${encodeURIComponent(communityId)}`,
          { params: { portfolioId } }
        );

      setNotification({
        type: 'success',
        message: 'Community access removed',
      });
      broadcastTenantDataUpdate();

      const fallbackAccess = accessEntries.filter(
        (access) =>
          access.portfolioId !== portfolioId || access.communityId !== communityId
      );
      const refreshed = await apiClient.get(
        `${API_BASE}/tenant-access/tenants/${encodeURIComponent(tenantAccess.emailNorm)}/grants`
      );
      const data = refreshed.data;
      setTenantAccess(data);
      updateAccessTracking(
        tenantAccess.emailNorm,
        data?.granted?.communityAccess || []
      );
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to remove community access',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAllAccess = async () => {
    if (!tenantAccess?.emailNorm) {
      return;
    }

    try {
      setLoading(true);
        await apiClient.delete(
          `${API_BASE}/tenant-access/tenants/${encodeURIComponent(tenantAccess.emailNorm)}`
        );

      setNotification({
        type: 'success',
        message: 'All community access removed (tenant record preserved)',
      });
      broadcastTenantDataUpdate();

      const fallbackAccess: Array<{ portfolioId: string; communityId: string }> = [];
      const refreshed = await apiClient.get(
        `${API_BASE}/tenant-access/tenants/${encodeURIComponent(tenantAccess.emailNorm)}/grants`
      );
      const data = refreshed.data;
      setTenantAccess(data);
      updateAccessTracking(
        tenantAccess.emailNorm,
        data?.granted?.communityAccess || []
      );
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to remove all community access',
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteTenantByEmail = async (emailNorm: string, closeModal: boolean) => {
    try {
      setLoading(true);
      await apiClient.delete(`${API_BASE}/tenant/${encodeURIComponent(emailNorm)}`);

      setNotification({
        type: 'success',
        message: 'Tenant deleted',
      });
      broadcastTenantDataUpdate();

      setAllTenants((prev) => prev.filter((tenant) => tenant.emailNorm !== emailNorm));
      setTenantAccessMap((prev) => {
        const next = { ...prev };
        delete next[emailNorm];
        return next;
      });
      setAccessSet((prev) => {
        const next = new Set(prev);
        next.delete(emailNorm);
        return next;
      });

      if (closeModal) {
        closeManageModal();
      }

      await loadAllTenants();
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to delete tenant',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTenant = async () => {
    if (!tenantAccess?.emailNorm) {
      return;
    }

    if (accessEntries.length > 0) {
      setNotification({
        type: 'error',
        message: 'Remove all access before deleting this tenant.',
      });
      return;
    }

    await deleteTenantByEmail(tenantAccess.emailNorm, true);
  };

  const openManageModal = (email?: string) => {
    if (email) {
      setLookupEmail(email);
      lookupTenantAccess(email);
    }
    setIsManageOpen(true);
  };

  const closeManageModal = () => {
    setIsManageOpen(false);
    setTenantAccess(null);
    setEditFirstName('');
    setEditLastName('');
    setModalPortfolioId('');
    setModalCommunityId('');
    setModalCommunities([]);
  };

  const handleModalPortfolioChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const portfolioId = e.target.value;
    setModalPortfolioId(portfolioId);
    setModalCommunityId('');
    loadModalCommunities(portfolioId);
  };

  const handleModalGrantAccess = async () => {
    if (!tenantAccess?.emailNorm) {
      return;
    }

    if (!modalPortfolioId || !modalCommunityId) {
      setNotification({
        type: 'error',
        message: 'Select a portfolio and community to grant access.',
      });
      return;
    }

    const alreadyGranted = tenantAccess.granted.communityAccess?.some(
      (access: { portfolioId: string; communityId: string }) =>
        access.portfolioId === modalPortfolioId && access.communityId === modalCommunityId
    );

    if (alreadyGranted) {
      setNotification({
        type: 'error',
        message: 'Tenant already has access to this community.',
      });
      return;
    }

    try {
      setLoading(true);
      await apiClient.post(
        `${API_BASE}/tenant-access`,
        {
          tenant: {
            email: tenantAccess.emailNorm,
            firstName: editFirstName || undefined,
            lastName: editLastName || undefined,
          },
          grant: {
            portfolioId: modalPortfolioId,
            communityId: modalCommunityId,
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      setNotification({
        type: 'success',
        message: 'Community access granted.',
      });
      broadcastTenantDataUpdate();
      setModalCommunityId('');
      await lookupTenantAccess(tenantAccess.emailNorm);
      await loadAllTenants();
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to grant community access',
      });
    } finally {
      setLoading(false);
    }
  };

  const originalFirstName = formatName(tenantAccess?.firstName);
  const originalLastName = formatName(tenantAccess?.lastName);
  const isNameDirty =
    Boolean(tenantAccess) &&
    (editFirstName !== originalFirstName || editLastName !== originalLastName);
  const hasMeaningfulName = Boolean(editFirstName.trim() || editLastName.trim());
  const canSaveName = isNameDirty && hasMeaningfulName;
  const accessEntries = (tenantAccess?.granted?.communityAccess || []) as Array<{
    portfolioId: string;
    communityId: string;
  }>;
  const canDeleteTenant = Boolean(tenantAccess?.emailNorm) && accessEntries.length === 0;
  const filteredTenants = React.useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) {
      return allTenants;
    }

    return allTenants.filter((tenant) => getTenantSearchText(tenant).includes(normalized));
  }, [allTenants, searchTerm, getTenantSearchText]);

  const filtersSummary = React.useMemo(() => {
    const filters: string[] = [];
    if (filterPortfolioId) {
      filters.push(`Portfolio=${portfolioNameMap[filterPortfolioId] || filterPortfolioId}`);
    }
    if (filterCommunityId) {
      const key = `${filterPortfolioId}:${filterCommunityId}`;
      filters.push(`Community=${communityNameMap[key] || filterCommunityId}`);
    }
    if (searchTerm.trim()) {
      filters.push(`Search=${searchTerm.trim()}`);
    }
    return filters.length ? filters.join(', ') : 'None';
  }, [filterPortfolioId, filterCommunityId, portfolioNameMap, communityNameMap, searchTerm]);

  const getCommunityDisplay = (accessList: Array<{ portfolioId: string; communityId: string }>) => {
    if (!accessList.length) {
      return { primary: null as any, extras: [] as Array<{ name: string; link: string }> };
    }

    const sorted = [...accessList]
      .map((access) => {
        const key = `${access.portfolioId}:${access.communityId}`;
        const name = communityNameMap[key] || access.communityId || 'Community';
        const link = `/portfolios/${encodeURIComponent(access.portfolioId)}/communities?communityId=${encodeURIComponent(access.communityId)}`;
        return { name, link };
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    return { primary: sorted[0], extras: sorted.slice(1) };
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.header}>
        <div>
          <h1>Tenant Management</h1>
          <p className={styles.subtitle}>
            Grant and manage tenant access to communities across portfolios. Existing access is updated
            silently to support multiple communities per tenant.
          </p>
        </div>
        <div className={styles.headerActions}>
          <PrimaryButton
            text="Create Tenant"
            onClick={() => openCreateTenantModal()}
          />
        </div>
      </div>

      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onDismiss={() => setNotification(null)}
        />
      )}

      <div className={styles.formCard}>
          <h2>Tenant Directory + Manage</h2>
          <p className={styles.helpText}>
            Filters show access status for the selected portfolio/community. The list always
            includes every tenant, even without grants.
          </p>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Portfolio (Filter)</label>
              <select value={filterPortfolioId} onChange={handleListPortfolioChange}>
                <option value="">-- All Portfolios --</option>
                {portfolios.map((portfolio) => (
                  <option key={portfolio.portfolioId} value={portfolio.portfolioId}>
                    {portfolio.friendlyName}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Community (Filter)</label>
              <select
                value={filterCommunityId}
                onChange={handleListCommunityChange}
                disabled={!filterPortfolioId}
              >
                <option value="">-- All Communities --</option>
                {filterCommunities.map((community) => (
                  <option key={community.communityId} value={community.communityId}>
                    {community.friendlyName || community.displayName || community.address || community.communityId}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.searchRow}>
            <div className={styles.formGroup}>
              <label>Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, portfolio, or community"
              />
            </div>
          </div>

          <div className={styles.formActions}>
            <PrimaryButton
              type="button"
              text={tenantsLoading ? 'Refreshing...' : 'Refresh Tenants'}
              onClick={loadAllTenants}
              disabled={tenantsLoading}
            />
            <DefaultButton
              text="Clear Filters"
              onClick={handleClearFilters}
              disabled={accessLoading}
            />
          </div>

          <TenantDirectoryTable
            tenants={filteredTenants}
            totalCount={allTenants.length}
            loading={tenantsLoading}
            filtersSummary={filtersSummary}
            onClearFilters={handleClearFilters}
            onRefresh={loadAllTenants}
            emptyState={{
              isFiltered: filtersSummary !== 'None',
              emptyMessage: 'No tenants loaded yet.',
              filteredMessage: 'No results. Clear filters.',
            }}
            getAccessList={getAccessList}
            getPortfolioName={(portfolioId) =>
              portfolioNameMap[portfolioId] || portfolioId
            }
            getCommunityName={(portfolioId, communityId) =>
              communityNameMap[`${portfolioId}:${communityId}`] || communityId || 'Community'
            }
            onManage={(email) => openManageModal(email)}
            onDelete={(email) => {
              openConfirm({
                title: 'Delete Tenant?',
                body: (
                  <div>
                    <p>This will permanently delete {email}.</p>
                    <p>This action cannot be undone.</p>
                  </div>
                ),
                confirmLabel: 'Delete',
                isDestructive: true,
                requireText: email,
                requireTextLabel: `Type ${email} to confirm`,
                onConfirm: () => deleteTenantByEmail(email, false),
              });
            }}
            isDeleteDisabled={(_email, accessList) => accessList.length > 0}
            getDeleteTooltip={(_email, accessList) =>
              accessList.length > 0
                ? 'Remove all access before deleting this tenant.'
                : 'Delete tenant'
            }
            onCopy={handleCopy}
            formatName={formatName}
          />

          <Modal
            isOpen={isManageOpen}
            onDismiss={closeManageModal}
            isBlocking={false}
            containerClassName={styles.manageModal}
          >
            {notification && (
              <Notification
                type={notification.type}
                message={notification.message}
                onDismiss={() => setNotification(null)}
              />
            )}
            <div className={styles.manageHeader}>
              <div>
                <h3>Manage Tenant</h3>
                <p className={styles.manageSubtext}>{tenantAccess?.emailNorm || lookupEmail}</p>
              </div>
              <button
                type="button"
                className={styles.iconButton}
                onClick={closeManageModal}
                aria-label="Close"
                title="Close"
              >
                <Icon iconName="Cancel" />
              </button>
            </div>
            {!tenantAccess ? (
              <div className={styles.emptyState}>Select a tenant to manage.</div>
            ) : (
              <>
                <div className={styles.sectionCard}>
                  <div className={styles.sectionHeader}>
                    <h3>Tenant Profile</h3>
                  </div>
                  <div className={styles.manageGrid}>
                    <div className={styles.manageCard}>
                      <span className={styles.label}>Email</span>
                      <span className={styles.value}>{tenantAccess.emailNorm}</span>
                    </div>
                    <div className={styles.manageCard}>
                      <span className={styles.label}>First Name</span>
                      <input
                        type="text"
                        value={editFirstName}
                        onChange={(e) => setEditFirstName(capitalizeFirst(e.target.value))}
                        placeholder="First name"
                      />
                    </div>
                    <div className={styles.manageCard}>
                      <span className={styles.label}>Last Name</span>
                      <input
                        type="text"
                        value={editLastName}
                        onChange={(e) => setEditLastName(capitalizeFirst(e.target.value))}
                        placeholder="Last name"
                      />
                    </div>
                    <div className={styles.manageCardActions}>
                      {canSaveName && (
                        <PrimaryButton
                          text={loading ? 'Saving...' : 'Save'}
                          onClick={handleUpdateTenantName}
                          disabled={loading}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.sectionCard}>
                  <div className={styles.sectionHeader}>
                    <h3>Access Configuration</h3>
                    <div className={styles.headerActions}>
                      <DefaultButton
                        text="Remove All Access"
                        onClick={() => {
                          const email = tenantAccess?.emailNorm || 'this tenant';
                          openConfirm({
                            title: 'Remove All Access?',
                            body: (
                              <div>
                                <p>This will remove all community access for {email}.</p>
                                <p>This action cannot be undone.</p>
                              </div>
                            ),
                            confirmLabel: 'Remove',
                            requireText: 'DELETE',
                            requireTextLabel: 'Type DELETE to confirm',
                            onConfirm: handleRemoveAllAccess,
                          });
                        }}
                        disabled={loading}
                      />
                      <DefaultButton
                        text="Delete Tenant"
                        onClick={() => {
                          const email = tenantAccess?.emailNorm || 'this tenant';
                          openConfirm({
                            title: 'Delete Tenant?',
                            body: (
                              <div>
                                <p>This will permanently delete {email}.</p>
                                <p>This action cannot be undone.</p>
                              </div>
                            ),
                            confirmLabel: 'Delete',
                            isDestructive: true,
                            requireText: tenantAccess?.emailNorm || 'DELETE',
                            requireTextLabel: `Type ${tenantAccess?.emailNorm || 'DELETE'} to confirm`,
                            onConfirm: handleDeleteTenant,
                          });
                        }}
                        disabled={loading || !canDeleteTenant}
                      />
                    </div>
                  </div>
                  <p className={styles.helpText}>
                    Removing access does not delete the tenant record.
                  </p>
                  <div className={styles.manageSection}>
                    <div className={styles.manageSectionHeader}>
                      <span className={styles.label}>Community Access</span>
                      <label className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={showIds}
                          onChange={(e) => setShowIds(e.target.checked)}
                        />
                        Show IDs
                      </label>
                    </div>
                    <p className={styles.helpText}>
                      Tenants can be associated with multiple communities across different portfolios.
                      Removing access does not delete the tenant record.
                    </p>
                    {accessEntries.length > 0 ? (
                      <StandardTable
                        items={accessEntries}
                        totalCount={accessEntries.length}
                        loading={loading}
                        filtersSummary="None"
                        onClearFilters={() => undefined}
                        onRefresh={() => lookupTenantAccess(tenantAccess.emailNorm)}
                        emptyState={{
                          isFiltered: false,
                          emptyMessage: 'No access configured.',
                          filteredMessage: 'No results. Clear filters.',
                        }}
                        renderTable={(paged) => (
                          <table className={styles.table}>
                            <thead>
                              <tr>
                                <th>Portfolio</th>
                                <th>Community</th>
                                <th>Actions</th>
                                {showIds && (
                                  <>
                                    <th>Portfolio ID</th>
                                    <th>Community ID</th>
                                  </>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {paged.map((access) => {
                                const portfolioName =
                                  portfolioNameMap[access.portfolioId] || access.portfolioId;
                                const communityKey = `${access.portfolioId}:${access.communityId}`;
                                const communityName =
                                  communityNameMap[communityKey] || access.communityId;
                                const portfolioLink = `/portfolios/${encodeURIComponent(
                                  access.portfolioId
                                )}/communities`;
                                const communityLink = `${portfolioLink}?communityId=${encodeURIComponent(
                                  access.communityId
                                )}`;

                                return (
                                  <tr key={communityKey}>
                                    <td>
                                      <span className={styles.inlineCopy}>
                                        <EntityLink
                                          to={portfolioLink}
                                          label={portfolioName}
                                          className={styles.accessLink}
                                        />
                                        <button
                                          type="button"
                                          className={styles.iconButtonInline}
                                          onClick={() => handleCopy(access.portfolioId, 'Portfolio ID')}
                                          aria-label="Copy portfolio ID"
                                          title="Copy portfolio ID"
                                        >
                                          <Icon iconName="Copy" />
                                        </button>
                                      </span>
                                      <input
                                        className={styles.hiddenField}
                                        value={access.portfolioId}
                                        readOnly
                                        aria-hidden="true"
                                        tabIndex={-1}
                                      />
                                    </td>
                                    <td>
                                      <EntityLink
                                        to={communityLink}
                                        label={communityName}
                                        className={styles.accessLink}
                                      />
                                      <input
                                        className={styles.hiddenField}
                                        value={access.communityId}
                                        readOnly
                                        aria-hidden="true"
                                        tabIndex={-1}
                                      />
                                      <button
                                        type="button"
                                        className={styles.iconButtonInline}
                                        onClick={() => handleCopy(access.communityId, 'Community ID')}
                                        aria-label="Copy community ID"
                                        title="Copy community ID"
                                      >
                                        <Icon iconName="Copy" />
                                      </button>
                                    </td>
                                    <td>
                                      <DefaultButton
                                        text="Remove Access"
                                        onClick={() => {
                                          const communityKey = `${access.portfolioId}:${access.communityId}`;
                                          const communityName =
                                            communityNameMap[communityKey] || access.communityId;
                                          openConfirm({
                                            title: 'Remove Access?',
                                            body: (
                                              <div>
                                                <p>This will remove access for {communityName}.</p>
                                                <p>This action cannot be undone.</p>
                                              </div>
                                            ),
                                            confirmLabel: 'Remove',
                                            onConfirm: () =>
                                              handleRemoveCommunityAccess(access.portfolioId, access.communityId),
                                          });
                                        }}
                                        disabled={loading}
                                      />
                                    </td>
                                    {showIds && (
                                      <>
                                        <td className={styles.monospace}>
                                          <span className={styles.inlineCopy}>
                                            {access.portfolioId}
                                            <button
                                              type="button"
                                              className={styles.iconButtonInline}
                                              onClick={() => handleCopy(access.portfolioId, 'Portfolio ID')}
                                              aria-label="Copy portfolio ID"
                                              title="Copy portfolio ID"
                                            >
                                              <Icon iconName="Copy" />
                                            </button>
                                          </span>
                                        </td>
                                        <td className={styles.monospace}>
                                          <span className={styles.inlineCopy}>
                                            {access.communityId}
                                            <button
                                              type="button"
                                              className={styles.iconButtonInline}
                                              onClick={() => handleCopy(access.communityId, 'Community ID')}
                                              aria-label="Copy community ID"
                                              title="Copy community ID"
                                            >
                                              <Icon iconName="Copy" />
                                            </button>
                                          </span>
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      />
                    ) : (
                      <span className={styles.value}>None</span>
                    )}
                  </div>
                  <div className={styles.manageSection}>
                    <div className={styles.manageSectionHeader}>
                      <span className={styles.label}>Grant Access</span>
                    </div>
                    <div className={styles.grantRow}>
                      <div className={styles.formGroup}>
                        <label>Portfolio</label>
                        <select value={modalPortfolioId} onChange={handleModalPortfolioChange}>
                          <option value="">-- Select Portfolio --</option>
                          {portfolios.map((portfolio) => (
                            <option key={portfolio.portfolioId} value={portfolio.portfolioId}>
                              {portfolio.friendlyName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className={styles.formGroup}>
                        <label>Community</label>
                        <select
                          value={modalCommunityId}
                          onChange={(e) => setModalCommunityId(e.target.value)}
                          disabled={!modalPortfolioId}
                        >
                          <option value="">-- Select Community --</option>
                          {modalCommunities.map((community) => (
                            <option key={community.communityId} value={community.communityId}>
                              {community.friendlyName ||
                                community.displayName ||
                                community.address ||
                                community.communityId}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className={styles.grantActions}>
                        <PrimaryButton
                          text={loading ? 'Granting...' : 'Grant Access'}
                          onClick={handleModalGrantAccess}
                          disabled={loading || !modalPortfolioId || !modalCommunityId}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Modal>
        </div>

      <CreateTenantModal
        isOpen={isCreateOpen}
        portfolios={portfolios}
        communities={communities}
        loadCommunities={loadCommunities}
        initialValues={createDefaults}
        onDismiss={() => setIsCreateOpen(false)}
        onCreated={(created) => {
          setAllTenants((prev) => {
            const exists = prev.some((tenant) => tenant.emailNorm === created.emailNorm);
            if (exists) {
              return prev;
            }
            return [
              { emailNorm: created.emailNorm, firstName: created.firstName, lastName: created.lastName },
              ...prev,
            ];
          });

          setTenantAccessMap((prev) => ({
            ...prev,
            [created.emailNorm]: [
              { portfolioId: created.portfolioId, communityId: created.communityId },
              ...(prev[created.emailNorm] || []).filter(
                (access) =>
                  access.portfolioId !== created.portfolioId || access.communityId !== created.communityId
              ),
            ],
          }));

          setAccessSet((prev) => {
            if (!filterPortfolioId) {
              return prev;
            }
            const matchesPortfolio = created.portfolioId === filterPortfolioId;
            const matchesCommunity = !filterCommunityId || created.communityId === filterCommunityId;
            if (!matchesPortfolio || !matchesCommunity) {
              return prev;
            }
            const next = new Set(prev);
            next.add(created.emailNorm);
            return next;
          });
        }}
        onViewTenant={(emailNorm) => {
          setIsCreateOpen(false);
          openManageModal(emailNorm);
        }}
        onNotify={(type, message) => setNotification({ type, message })}
      />
      {confirmDialog}
    </div>
  );
};
